// Reporting Module Tour
// Analytics, reports, and data insights

export const reportingTour = {
  id: 'reporting',
  name: 'Reporting Module Tour',
  displayName: 'Track Your Performance',
  description: 'Measure what matters with powerful analytics and reports',
  estimatedDuration: '4 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Reporting
    {
      element: 'nav #sb_reporting',
      popover: {
        title: '📊 Welcome to Reporting',
        description: `
          Data drives decisions! The Reporting module shows you what's working and 
          what's not. Track lead sources, campaign performance, pipeline metrics, 
          and more. If you can't measure it, you can't improve it!
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: Report Types
    {
      popover: {
        title: '📈 Available Reports',
        description: `
          Access multiple report categories:
          • **Lead Source**: Where do your leads come from?
          • **Pipeline**: Opportunities, win rates, deal values
          • **Campaign**: Email/SMS performance
          • **Attribution**: Which marketing touches lead to sales?
          • **Activity**: Team performance, contact engagement
          • **Custom Reports**: Build your own
          
          Each report helps answer specific business questions.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 3: Dashboard Overview
    {
      element: '[class*="dashboard"], [href*="dashboard"]',
      popover: {
        title: '🎯 Reporting Dashboard',
        description: `
          Your reporting dashboard shows key metrics at a glance:
          • Total contacts
          • Opportunities in pipeline
          • Revenue won this month
          • Campaign performance
          • Lead sources
          
          Customize which widgets appear to focus on your most important metrics.
        `,
        side: 'left',
        align: 'start'
      }
    },

    // Step 4: Lead Source Report
    {
      element: '[href*="lead-source"], [class*="attribution"]',
      popover: {
        title: '🎯 Lead Source Tracking',
        description: `
          Know where your best leads come from:
          • **Google Ads**: Paid search
          • **Facebook**: Social advertising
          • **Referral**: Word of mouth
          • **Direct**: Typed URL directly
          • **Organic**: Found via search
          
          This report shows which channels bring the most leads AND which 
          convert to customers. Focus budget on what works!
        `,
        side: 'left',
        align: 'center',
        proTip: 'Use UTM parameters in your URLs to track specific campaigns within each channel!'
      }
    },

    // Step 5: Pipeline Reports
    {
      element: '[href*="pipeline"], [href*="opportunity"]',
      popover: {
        title: '💰 Pipeline Analytics',
        description: `
          Deep dive into your sales pipeline:
          • **Conversion rates** by stage
          • **Average deal size**
          • **Sales velocity**: How fast deals close
          • **Win/loss rates**
          • **Revenue forecast** based on pipeline
          
          See which stages have bottlenecks and optimize your sales process!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 6: Campaign Performance
    {
      element: '[href*="campaign"], [href*="email-stats"]',
      popover: {
        title: '📧 Campaign Analytics',
        description: `
          Track your marketing campaign performance:
          • **Open rates**: Who opened your emails
          • **Click rates**: Who clicked links
          • **Unsubscribes**: Who opted out
          • **Best performing**: Which campaigns won
          • **Link clicks**: Which CTAs worked
          
          Use this data to write better subject lines and improve content!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 7: Attribution Reports
    {
      popover: {
        title: '🔍 Marketing Attribution',
        description: `
          See the complete customer journey:
          • **First touch**: What brought them in initially
          • **Last touch**: Final interaction before conversion
          • **Multi-touch**: Every touchpoint along the way
          
          Example: "Saw Facebook ad → Downloaded guide → Booked call → Purchased"
          
          Attribution shows which marketing efforts actually drive revenue!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 8: Date Range Filters
    {
      element: '[class*="date-picker"], input[type="date"]',
      popover: {
        title: '📅 Filter by Date Range',
        description: `
          View reports for specific time periods:
          • Today
          • This week/month/quarter/year
          • Last 30/60/90 days
          • Custom date range
          
          Compare periods: "This month vs last month" to track growth trends!
        `,
        side: 'left',
        align: 'center',
        proTip: 'Run reports on the same day each week for consistent week-over-week comparisons!'
      }
    },

    // Step 9: Exporting Reports
    {
      element: '[href*="export"], button:contains("Export")',
      popover: {
        title: '📥 Export Data',
        description: `
          Need data in Excel or Google Sheets? Export it!
          • **CSV**: Open in any spreadsheet program
          • **PDF**: Share with team or clients
          • **Schedule**: Auto-email reports weekly/monthly
          
          Use exports for deeper analysis, presentations, or integrating 
          with other business intelligence tools.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 10: Custom Reports
    {
      element: '[href*="custom"], button:contains("Create Report")',
      popover: {
        title: '🔧 Build Custom Reports',
        description: `
          Need a specific report that doesn't exist? Build your own!
          • Choose metrics to include
          • Select dimensions (by tag, by user, by source, etc.)
          • Set filters and conditions
          • Save for repeated use
          
          Custom reports let you answer unique questions specific to your business.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 11: Team Performance
    {
      element: '[href*="team"], [href*="user-activity"]',
      popover: {
        title: '👥 Team Activity Reports',
        description: `
          Track team member productivity:
          • Conversations handled per person
          • Response times
          • Opportunities closed
          • Tasks completed
          • Login frequency
          
          Use this data for coaching, recognition, and identifying training needs. 
          Not for micromanaging—for supporting your team!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 12: Setting Goals
    {
      popover: {
        title: '🎯 Set Performance Goals',
        description: `
          Many reports let you set goals:
          • "Close 20 deals this month"
          • "Reach 1,000 contacts by Q4"
          • "Maintain 25% email open rate"
          
          Visual indicators show progress toward goals. Green = on track, 
          red = behind. Keeps your team aligned and motivated!
        `,
        side: 'over',
        align: 'center',
        proTip: 'Set ambitious but achievable goals. Too easy = no growth. Too hard = demotivating.'
      }
    },

    // Step 13: Completion
    {
      popover: {
        title: '🎉 You\'re a Data-Driven Pro!',
        description: `
          Fantastic! You now know how to:
          ✅ View key business metrics
          ✅ Track lead sources and attribution
          ✅ Analyze pipeline performance
          ✅ Measure campaign effectiveness
          ✅ Export reports for deeper analysis
          ✅ Build custom reports
          ✅ Monitor team performance
          
          **Next Steps:**
          • Set up your most important dashboard widgets
          • Create a weekly report routine (check every Monday!)
          • Set goals for key metrics
          • Export a report and analyze trends
          
          Measure, improve, repeat! 📈
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Track Everything! →'
      }
    }
  ]
};
